import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { getDatesFromDateRange, PendingBookingsTable } from 'components/BookingsTable';
import Container from 'components/Container';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import SearchBar from 'components/SearchBar';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import QueryResultsLimitedBanner from 'components/QueryResultsLimitedBanner';
import { api } from 'common/frontend/api';
import { useQuery } from 'hooks';
import { useTranslation } from 'services/localization';
import { DateRangePicker } from 'components/DataGridDateRangePicker';
import { DataGridDateRange } from 'components/DataGridDateRangePicker/types';

const DEFAULT_LIMIT = 25;

const PendingBookingsView = () => {
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const locationFilterType = useSelector(ViewSelectors.bookingsViewLocationType);

	const [searchValue, setSearchValue] = useState<string>('');
	const [dateRange, setDateRange] = useState<DataGridDateRange>({
		type: 'default',
		limit: DEFAULT_LIMIT,
	});

	const locationId = useMemo(() => {
		return locationFilterType === 'current' ? activeLocationId : null;
	}, [locationFilterType, activeLocationId]);

	const [startAt, endAt] = useMemo(() => {
		return getDatesFromDateRange(dateRange).slice().reverse();
	}, [dateRange]);

	const query = useMemo(() => {
		let builder = api()
			.orders.get.where('shopId', '==', shopId)
			.where('rentalState', '==', 'BOOKED')
			.where('purchaseType', '==', 'sales');

		if (!!locationId) {
			builder = builder.where('startLocation.id', '==', locationId);
		}

		builder = builder.orderBy('created', 'desc');

		if (!!startAt) {
			builder = builder.startAt(startAt.toISOString());
		}

		if (!!endAt) {
			builder = builder.endAt(endAt.toISOString());
		}

		return builder;
	}, [shopId, locationId, startAt, endAt]);

	const bookings = useQuery({
		query,
		initialLimit: dateRange.type === 'default' ? dateRange.limit : 100,
	});

	return (
		<Container fluid>
			<Stack direction="row" spacing={2}>
				<Box flex={1}>
					<SearchBar
						onChange={setSearchValue}
						placeholder={t('rentals.search.byNameOrProductCode') + '...'}
					/>
				</Box>
				<DateRangePicker
					value={dateRange}
					onChange={setDateRange}
					defaultItem={{
						limit: DEFAULT_LIMIT,
						label: t('common:times.latest', 'Latest'),
					}}
				/>
			</Stack>
			{dateRange.type !== 'default' && <QueryResultsLimitedBanner {...bookings} />}

			<PendingBookingsTable
				bookings={bookings.data ?? []}
				loading={bookings.loading}
				searchValue={searchValue}
			/>
		</Container>
	);
};

export default PendingBookingsView;
